import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import SaveBar from '@/components/forms/SaveBar'
import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardHeader } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { useAppForm } from '@/lib/form'
import {
  usePublishSeasonalSchedulesMutation,
  useSeasonalScheduleImpactMutation,
} from '@/lib/services/seasonal-content/seasonal-content.query'
import { seasonalSchedulesSchema } from '@/lib/services/seasonal-content/seasonal-content.schema'
import { getErrorMessage } from '@/lib/utils'
import { flattenSeasonalContentTagGroups } from '@/lib/services/merchandising-tags/merchandising-tags.utils'
import { useStore } from '@tanstack/react-form'
import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { SeasonalScheduleCreateSheet } from './SeasonalScheduleCreateSheet'
import {
  SeasonalScheduleDefaultsFields,
  SeasonalScheduleTagField,
  SeasonalScheduleTimingFields,
} from './SeasonalScheduleFields'

const profileValues = (profiles) => ({
  profiles: profiles.map((profile) => ({
    id: profile.id,
    timing_mode: profile.timing_mode,
    days_before: Math.abs(profile.draft_start_offset_days),
    days_after: Math.max(0, profile.draft_end_offset_days),
    start_month: profile.draft_start_month ?? 1,
    start_day: profile.draft_start_day ?? 1,
    end_month: profile.draft_end_month ?? 12,
    end_day: profile.draft_end_day ?? 31,
    end_is_last_day: Boolean(profile.draft_end_is_last_day),
    provision_new_organizations: profile.provision_new_organizations,
    default_enabled: profile.default_enabled,
  })),
  new_profiles: [],
})

const schedulePayload = (profile) => ({
  ...(profile.id ? { id: profile.id } : { seasonal_tag_id: Number(profile.seasonal_tag_id) }),
  timing_mode: profile.timing_mode,
  start_offset_days:
    profile.timing_mode === 'occasion_offset' ? -Math.abs(Number(profile.days_before)) : null,
  end_offset_days:
    profile.timing_mode === 'occasion_offset' ? Math.abs(Number(profile.days_after)) : null,
  start_month: profile.timing_mode === 'annual_range' ? Number(profile.start_month) : null,
  start_day: profile.timing_mode === 'annual_range' ? Number(profile.start_day) : null,
  end_month: profile.timing_mode === 'annual_range' ? Number(profile.end_month) : null,
  end_day:
    profile.timing_mode === 'annual_range' && !profile.end_is_last_day
      ? Number(profile.end_day)
      : null,
  end_is_last_day:
    profile.timing_mode === 'annual_range' ? Boolean(profile.end_is_last_day) : false,
  provision_new_organizations: profile.provision_new_organizations,
  default_enabled: profile.default_enabled,
})

export function SeasonalScheduleList({
  profiles,
  merchandisingTagGroups,
  isReadOnly,
  organizationId,
}) {
  const publishMutation = usePublishSeasonalSchedulesMutation({ organizationId })
  const impactMutation = useSeasonalScheduleImpactMutation({ organizationId })
  const [createScheduleOpen, setCreateScheduleOpen] = useState(false)
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [pendingPublish, setPendingPublish] = useState(null)
  const form = useAppForm({
    defaultValues: profileValues(profiles),
    validators: {
      onSubmit: seasonalSchedulesSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          const originalById = new Map(
            profileValues(profiles).profiles.map((row) => [row.id, schedulePayload(row)])
          )
          const values = {
            profiles: value.profiles
              .filter((profile) => {
                const original = originalById.get(profile.id)
                return JSON.stringify(schedulePayload(profile)) !== JSON.stringify(original)
              })
              .map(schedulePayload),
            new_profiles: value.new_profiles.map(schedulePayload),
          }
          const impacts = await Promise.all(
            values.profiles.map(async (profile) => {
              const response = await impactMutation.mutateAsync({ profileId: profile.id })
              return response?.data ?? response
            })
          )
          setPendingPublish({
            values,
            automaticScheduleCount: impacts.reduce(
              (total, impact) => total + Number(impact?.automatic_schedule_count ?? 0),
              0
            ),
            liveImpactCount: impacts.reduce(
              (total, impact) => total + Number(impact?.live_impact_count ?? 0),
              0
            ),
          })
          setConfirmationOpen(true)
        } catch (error) {
          toast.error('Could not calculate schedule impact', {
            description: getErrorMessage(error),
          })
          if (error?.response?.data?.errors) return { fields: error.response.data.errors }
        }
      },
    },
  })
  const publishPendingSchedules = async () => {
    if (!pendingPublish) {
      return
    }

    try {
      const response = await publishMutation.mutateAsync({ values: pendingPublish.values })
      form.reset(profileValues(response?.data ?? profiles))
      setPendingPublish(null)
      toast.success('Default schedules saved')
    } catch (error) {
      toast.error('Could not save schedules', { description: getErrorMessage(error) })
      throw error
    }
  }
  const seasonalTagOptions = flattenSeasonalContentTagGroups(merchandisingTagGroups)
  const usedSeasonalTagIds = new Set(profiles.map((profile) => Number(profile.seasonal_tag_id)))
  const availableSeasonalTags = seasonalTagOptions.filter(
    (tag) => !usedSeasonalTagIds.has(Number(tag.id))
  )
  const newScheduleRows = useStore(form.store, (state) => state.values.new_profiles)
  const selectedNewSeasonalTagIds = newScheduleRows
    .map((profile) => Number(profile.seasonal_tag_id))
    .filter(Boolean)
  const remainingSeasonalTags = availableSeasonalTags.filter(
    (tag) => !selectedNewSeasonalTagIds.includes(Number(tag.id))
  )

  const addSchedule = (schedule) => {
    const current = form.getFieldValue('new_profiles') ?? []
    form.setFieldValue('new_profiles', [schedule, ...current])
  }

  return (
    <>
      <form.AppForm>
        <form.Form>
          <Card className="overflow-hidden py-0">
            <CardHeader className="border-b px-4 py-4 sm:px-5">
              <div>
                <Typography variant="h3">Default schedules</Typography>
                <Typography variant="supporting">
                  Edit timing inline, then save every change together.
                </Typography>
              </div>
              {!isReadOnly ? (
                <CardAction>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={
                      !remainingSeasonalTags.length ||
                      newScheduleRows.some((row) => !row.seasonal_tag_id)
                    }
                    onClick={() => setCreateScheduleOpen(true)}
                  >
                    <Plus className="size-4" /> Create schedule
                  </Button>
                </CardAction>
              ) : null}
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y" role="list" aria-label="Default schedules">
                <form.Subscribe selector={(state) => state.values.new_profiles}>
                  {(newProfiles) =>
                    newProfiles.map((profile, index) => {
                      const selectedElsewhere = new Set(
                        newProfiles
                          .filter((_, candidateIndex) => candidateIndex !== index)
                          .map((candidate) => Number(candidate.seasonal_tag_id))
                      )
                      const options = availableSeasonalTags.filter(
                        (tag) => !selectedElsewhere.has(Number(tag.id))
                      )

                      return (
                        <ScheduleRow
                          key={`new-${index}`}
                          form={form}
                          path={`new_profiles.${index}`}
                          occasionOptions={options}
                          isReadOnly={isReadOnly}
                          onRemove={() => {
                            const current = form.getFieldValue('new_profiles') ?? []
                            form.setFieldValue(
                              'new_profiles',
                              current.filter((_, candidateIndex) => candidateIndex !== index)
                            )
                          }}
                        />
                      )
                    })
                  }
                </form.Subscribe>
                {profiles.map((profile, index) => (
                  <ScheduleRow
                    key={profile.id}
                    form={form}
                    path={`profiles.${index}`}
                    profile={profile}
                    isReadOnly={isReadOnly}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
          {!isReadOnly ? (
            <SaveBar
              mutation={publishMutation}
              submitButtonText="Save changes"
              resetButtonText="Discard"
            />
          ) : null}
          <ConfirmationDialog
            open={confirmationOpen}
            onOpenChange={setConfirmationOpen}
            title="Publish default schedule changes?"
            description={`${pendingPublish?.automaticScheduleCount ?? 0} florist automations use these schedules; ${pendingPublish?.liveImpactCount ?? 0} are live now. Saving publishes the new timing immediately.`}
            confirmText="Save changes"
            isPending={publishMutation.isPending}
            onConfirm={publishPendingSchedules}
            trigger={null}
          />
        </form.Form>
      </form.AppForm>
      {!isReadOnly ? (
        <SeasonalScheduleCreateSheet
          open={createScheduleOpen}
          onOpenChange={setCreateScheduleOpen}
          options={remainingSeasonalTags}
          onAdd={addSchedule}
        />
      ) : null}
    </>
  )
}

function ScheduleRow({ form, path, profile = null, occasionOptions, isReadOnly, onRemove }) {
  const selectedSeasonalTagId = useStore(form.store, (state) => {
    const segments = path.split('.')
    return state.values?.[segments[0]]?.[Number(segments[1])]?.seasonal_tag_id
  })
  const selectedSeasonalTag = occasionOptions?.find(
    (option) => Number(option.id) === Number(selectedSeasonalTagId)
  )
  const rowLabel = profile?.name
    ? `${profile.name} schedule`
    : selectedSeasonalTag?.label
      ? `${selectedSeasonalTag.label} schedule`
      : 'New schedule'

  return (
    <div
      role="listitem"
      aria-label={rowLabel}
      className="grid gap-4 px-4 py-4 @container/schedule sm:px-5 xl:grid-cols-[minmax(14rem,0.8fr)_minmax(22rem,1.4fr)_minmax(18rem,1fr)_2.5rem] xl:items-center"
    >
      <div className="min-w-0 space-y-1">
        {profile ? (
          <>
            <div className="font-medium">{profile.name}</div>
            <Typography variant="caption" className="mt-0.5 block">
              {profile.automatic_schedule_count} automatic · {profile.live_impact_count} live · rev{' '}
              {profile.published_revision ?? '—'}
            </Typography>
          </>
        ) : (
          <SeasonalScheduleTagField form={form} path={path} options={occasionOptions} />
        )}
      </div>
      <SeasonalScheduleTimingFields form={form} path={path} isReadOnly={isReadOnly} />
      <SeasonalScheduleDefaultsFields form={form} path={path} isReadOnly={isReadOnly} />
      <div>
        {onRemove ? (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label="Remove new schedule"
            onClick={onRemove}
          >
            <Trash2 className="size-4" />
          </Button>
        ) : null}
      </div>
    </div>
  )
}
